import { useFetchers } from 'react-router';

import { useRequestInfo } from '@veraclins-dev/react-utils';

import { type RequestInfo, type Theme } from './types';

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();

  const themeFetcher = fetchers.find((f) => f.formAction === '/common/theme');

  return themeFetcher?.formData?.get('theme') as Theme | 'system' | undefined;
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme(): Theme {
  const { hints, userPrefs } = useRequestInfo<{ requestInfo: RequestInfo }>();
  const optimisticMode = useOptimisticThemeMode();

  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode;
  }

  return userPrefs.theme ?? hints.theme;
}

/**
 * @returns the current theme mode (including 'system') for display purposes
 */
export function useThemeMode(): Theme | 'system' {
  const { userPrefs } = useRequestInfo<{ requestInfo: RequestInfo }>();
  const optimisticMode = useOptimisticThemeMode();

  if (optimisticMode) {
    return optimisticMode;
  }

  // If no cookie is set, return 'system' to indicate using system preference
  return userPrefs.theme ?? 'system';
}
